// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import EngineeringIcon from '@mui/icons-material/Engineering'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import GroupsIcon from '@mui/icons-material/Groups'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall } from '@/assets/theme/fonts'

export default function AudienceSection() {
  const { t } = useTranslation('servicesOtAwarenessTraining')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const groups: { icon: ReactElement; title: string; text: string }[] = [
    {
      icon: <EngineeringIcon sx={iconSx} />,
      title: t('audience.items.01.title'),
      text: t('audience.items.01.text')
    },
    {
      icon: <ManageAccountsIcon sx={iconSx} />,
      title: t('audience.items.02.title'),
      text: t('audience.items.02.text')
    },
    {
      icon: <GroupsIcon sx={iconSx} />,
      title: t('audience.items.03.title'),
      text: t('audience.items.03.text')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('audience.eyebrow')}
        title={t('audience.title')}
        intro={[t('audience.intro')]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {groups.map(group => (
          <Box
            key={group.title}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              background: themeColors.kroemtech.background,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.75rem',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ lineHeight: 0, marginBottom: '1.1rem' }}>
              {group.icon}
            </Box>

            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: themeColors.kroemtech.dunkelblau,
                marginBottom: '0.7rem'
              }}
            >
              {group.title}
            </Typography>

            <Typography
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.65,
                color: themeColors.gray.defaultText
              }}
            >
              {group.text}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '48rem',
          marginTop: '2rem'
        }}
      >
        {t('audience.note')}
      </Typography>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
